import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Icono, IconBubble } from "./iconos";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

const VERDE   = "#7BC74D";
const VIOLETA = "#7c3aed";

const fmt = (n, dec = 2) =>
    n == null ? "—" : Number(n).toLocaleString("es-AR", { minimumFractionDigits: dec, maximumFractionDigits: dec });

const fmtPesos = (n) => n == null ? "—" : `US$ ${fmt(n, 2)}`;

const STATUS_BADGE = {
    ACTIVE:   { label: "Activa",   bg: "#e8f5e0", color: "#3a7d1e" },
    PAUSED:   { label: "Pausada",  bg: "#fff3cd", color: "#856404" },
    ARCHIVED: { label: "Archivada", bg: "#e9ecef", color: "#6c757d" },
};

function PctBadge({ pct }) {
    if (pct == null) return <span style={{ fontSize: "0.72rem", color: "#bbb" }}>—</span>;
    const up = pct >= 0;
    return (
        <span style={{
            fontSize: "0.72rem",
            fontWeight: 700,
            color: up ? "#3a7d1e" : "#c0392b",
        }}>
            {up ? "+" : ""}{pct}%
            <span style={{ color: "#aaa", fontWeight: 400, marginLeft: 4 }}>vs período anterior</span>
        </span>
    );
}

function KpiCard({ icono, label, value, pct, color }) {
    const c = color || VERDE;
    return (
        <div className="col-6 col-md-3 mb-3">
            <div className="card h-100 border-0 shadow-sm">
                <div className="card-body py-3 px-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <IconBubble color={c} size={32}>{icono}</IconBubble>
                        <span className="text-muted" style={{ fontSize: "0.78rem", fontWeight: 500 }}>{label}</span>
                    </div>
                    <div style={{ fontSize: "1.65rem", fontWeight: 700, color: "#222", lineHeight: 1.1, marginBottom: 4 }}>
                        {value}
                    </div>
                    <PctBadge pct={pct} />
                </div>
            </div>
        </div>
    );
}

export default function ZTCampanas() {
    const { darkMode } = useContext(DarkModeContext);
    const navegacion = useNavigate();
    const [autorizado, setAutorizado] = useState(false);
    const [cargando, setCargando] = useState(true);
    const [datos, setDatos] = useState(null);

    useEffect(() => {
        const init = async () => {
            const token = getToken();
            if (!token) { navegacion("/"); return; }
            try {
                const r = await fetch(process.env.REACT_APP_API_ZT_CAMPANAS, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ Token: token }),
                });
                if (r.status === 401 || r.status === 403) { navegacion("/"); return; }
                if (r.ok) {
                    const raw = await r.json();
                    setDatos({
                        campanas:        JSON.parse(raw.campanas  || "[]"),
                        insights:        JSON.parse(raw.insights  || "[]"),
                        diario:          JSON.parse(raw.diario    || "[]"),
                        leadsPorCampana: raw.leadsPorCampana || {},
                    });
                    setAutorizado(true);
                }
            } catch { navegacion("/"); }
            finally { setCargando(false); }
        };
        init();
    }, [navegacion]);

    if (cargando) return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <div className="spinner-border" style={{ color: VERDE }} />
        </div>
    );
    if (!autorizado || !datos) return null;

    // Mapa insights por campaña
    const insMap = {};
    datos.insights.forEach(i => { insMap[i.campaign_id] = i; });

    const rows = datos.campanas.map(c => {
        const ins   = insMap[c.id] || {};
        const gasto = parseFloat(ins.spend  || 0);
        const reach = parseFloat(ins.reach  || 0);
        const clics = parseFloat(ins.clicks || 0);
        const imp   = parseFloat(ins.impressions || 0);
        const leads = datos.leadsPorCampana[c.id] || 0;
        const cpl   = gasto > 0 && leads > 0 ? gasto / leads : null;
        const ctr   = imp   > 0 ? clics / imp * 100 : null;
        return { ...c, gasto, reach, clics, imp, leads, cpl, ctr };
    });

    // KPIs globales
    const totalLeads = rows.reduce((s, r) => s + r.leads, 0);
    const totalGasto = rows.reduce((s, r) => s + r.gasto, 0);
    const totalImp   = rows.reduce((s, r) => s + r.imp, 0);
    const totalClics = rows.reduce((s, r) => s + r.clics, 0);
    const cplProm    = totalLeads > 0 && totalGasto > 0 ? totalGasto / totalLeads : null;
    const ctrProm    = totalImp   > 0 ? totalClics / totalImp * 100 : null;

    // Ranking por leads
    const ranking = [...rows]
        .filter(r => r.leads > 0 || r.gasto > 0)
        .sort((a, b) => b.leads - a.leads)
        .slice(0, 5);

    // Alertas: CTR bajo, frecuencia alta (proxy: high spend + low CTR), creativo (low CTR)
    const alertas = [];
    rows.forEach(r => {
        if (r.ctr != null && r.ctr < 0.5 && r.gasto > 0)
            alertas.push({
                tipo: "ctr_bajo",
                color: "#c0392b",
                bg: "#fdecea",
                titulo: "CTR bajo",
                detalle: `La campaña ${r.name} tiene un CTR de ${fmt(r.ctr, 2)}%.`,
                icono: <Icono.ArrowDown />,
            });
    });
    rows.forEach(r => {
        if (r.cpl != null && cplProm && r.cpl > cplProm * 1.8)
            alertas.push({
                tipo: "cpl_alto",
                color: "#e67e22",
                bg: "#fff3cd",
                titulo: "Frecuencia alta",
                detalle: `La campaña ${r.name} tiene CPL ${fmt(r.cpl / cplProm, 1)}x sobre el promedio.`,
                icono: <Icono.ArrowUp />,
            });
    });
    if (rows.some(r => r.ctr != null && r.ctr < 0.3))
        alertas.push({
            tipo: "creativo",
            color: "#666",
            bg: "#f0f0f0",
            titulo: "Necesita nuevo creativo",
            detalle: "El rendimiento del creativo principal está por debajo del promedio.",
            icono: <Icono.ImageIcon />,
        });

    // Gráfico diario combinado
    const dailyMap = {};
    datos.diario.forEach(d => {
        const f = d.date_start || "?";
        if (!dailyMap[f]) dailyMap[f] = { spend: 0, leads: 0 };
        dailyMap[f].spend += parseFloat(d.spend || 0);
    });
    const dailyLabels = Object.keys(dailyMap).sort();
    const dailySpend  = dailyLabels.map(k => dailyMap[k].spend);
    const dailyLeads  = dailyLabels.map(() => 0);

    const chartData = {
        labels: dailyLabels,
        datasets: [
            {
                type: "bar",
                label: "Leads",
                data: dailyLeads,
                backgroundColor: "rgba(123,199,77,0.5)",
                borderRadius: 4,
                yAxisID: "y",
                order: 2,
            },
            {
                type: "line",
                label: "Inversión (US$)",
                data: dailySpend,
                borderColor: VIOLETA,
                backgroundColor: "rgba(124,58,237,0.08)",
                tension: 0.35,
                pointRadius: 4,
                pointBackgroundColor: VIOLETA,
                pointBorderColor: "#fff",
                pointBorderWidth: 2,
                fill: false,
                yAxisID: "y1",
                order: 1,
            },
        ],
    };

    return (
        <div className={darkMode ? "container-dark p-3" : "p-3"}>
            <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                <div>
                    <h4 className="fw-bold mb-1" style={{ color: "#222" }}>Rendimiento de campañas</h4>
                    <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
                        Analizá el desempeño de tus campañas y optimizá resultados.
                    </p>
                </div>
                <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: "0.78rem" }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: VERDE }} />
                    Actualizado hace 5 min
                </div>
            </div>

            {/* KPIs */}
            <div className="row g-2 mb-3">
                <KpiCard icono={<Icono.Users />}        label="Leads totales"   value={fmt(totalLeads, 0)} pct={null} color={VERDE} />
                <KpiCard icono={<Icono.Dollar />}       label="CPL promedio"    value={fmtPesos(cplProm)}  pct={null} color={VERDE} />
                <KpiCard icono={<Icono.Wallet />}       label="Inversión total" value={fmtPesos(totalGasto)} pct={null} color={VERDE} />
                <KpiCard icono={<Icono.MousePointer />} label="CTR promedio"    value={ctrProm != null ? `${fmt(ctrProm, 2)}%` : "—"} pct={null} color={VERDE} />
            </div>

            {/* Tabla comparación */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                    <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                        Comparación de campañas
                        <Icono.Filter color="#aaa" />
                    </h6>
                    <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                            <thead>
                                <tr style={{ borderBottom: "1px solid #eee" }}>
                                    <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999", paddingBottom: 10 }}>Campaña</th>
                                    <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }}>Objetivo</th>
                                    <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }} className="text-end">Leads</th>
                                    <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }} className="text-end">CPL</th>
                                    <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }} className="text-end">Inversión</th>
                                    <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }} className="text-end">CTR</th>
                                    <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }} className="text-end">Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 && (
                                    <tr><td colSpan={7} className="text-center text-muted py-4">Sin datos de campañas</td></tr>
                                )}
                                {rows.map(r => {
                                    const badge = STATUS_BADGE[r.status] || { label: r.status, bg: "#e9ecef", color: "#555" };
                                    return (
                                        <tr key={r.id} style={{ borderBottom: "1px solid #f5f5f5" }}>
                                            <td style={{ fontSize: "0.82rem", fontWeight: 600, color: "#222", padding: "12px 0" }}>
                                                <div className="d-flex align-items-center gap-2">
                                                    <Icono.Star color={VERDE} width={14} height={14} />
                                                    {r.name}
                                                </div>
                                            </td>
                                            <td style={{ fontSize: "0.78rem", color: "#666" }}>{r.objective || "—"}</td>
                                            <td className="text-end" style={{ fontSize: "0.82rem", fontWeight: 600 }}>{r.leads}</td>
                                            <td className="text-end" style={{ fontSize: "0.82rem", fontWeight: 600 }}>{fmtPesos(r.cpl)}</td>
                                            <td className="text-end" style={{ fontSize: "0.82rem" }}>{fmtPesos(r.gasto)}</td>
                                            <td className="text-end" style={{ fontSize: "0.82rem" }}>{r.ctr != null ? `${fmt(r.ctr, 2)}%` : "—"}</td>
                                            <td className="text-end">
                                                <span style={{
                                                    display: "inline-block",
                                                    fontSize: "0.7rem",
                                                    fontWeight: 600,
                                                    padding: "3px 10px",
                                                    borderRadius: 12,
                                                    backgroundColor: badge.bg,
                                                    color: badge.color,
                                                }}>
                                                    {badge.label}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Gráfico + Ranking + Alertas */}
            <div className="row g-3">
                <div className="col-12 col-lg-5">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold mb-0">Inversión y leads por día</h6>
                                <span className="text-muted" style={{ fontSize: "0.75rem", border: "1px solid #ddd", borderRadius: 6, padding: "3px 10px" }}>
                                    Diario
                                </span>
                            </div>
                            {dailyLabels.length > 0 ? (
                                <Bar
                                    data={chartData}
                                    options={{
                                        responsive: true,
                                        interaction: { mode: "index", intersect: false },
                                        plugins: {
                                            legend: {
                                                position: "top", align: "start",
                                                labels: { boxWidth: 8, boxHeight: 8, usePointStyle: true, font: { size: 11 } },
                                            },
                                        },
                                        scales: {
                                            y:  { position: "left", beginAtZero: true, ticks: { font: { size: 10 } }, grid: { color: "rgba(0,0,0,0.05)" } },
                                            y1: { position: "right", beginAtZero: true, ticks: { font: { size: 10 }, callback: v => `US$ ${v}` }, grid: { drawOnChartArea: false } },
                                            x:  { grid: { display: false }, ticks: { font: { size: 10 } } },
                                        },
                                    }}
                                />
                            ) : (
                                <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 180, color: "#bbb" }}>
                                    <span style={{ fontSize: "0.85rem" }}>Sin datos diarios</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-12 col-lg-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                Ranking de campañas <Icono.Trophy color="#aaa" width={14} height={14} />
                            </h6>
                            {ranking.length === 0 ? (
                                <p className="text-muted" style={{ fontSize: "0.82rem" }}>Sin leads aún.</p>
                            ) : ranking.map((c, i) => (
                                <div key={c.id} className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="d-flex align-items-center gap-2" style={{ flex: 1, minWidth: 0 }}>
                                        <span style={{ color: "#999", fontSize: "0.85rem", fontWeight: 600, width: 14 }}>{i + 1}</span>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                fontSize: "0.78rem",
                                                fontWeight: 600,
                                                color: "#222",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}>{c.name}</div>
                                        </div>
                                    </div>
                                    <div className="text-end" style={{ marginLeft: 8 }}>
                                        <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#222" }}>{c.leads || "—"}</div>
                                        <div style={{ fontSize: "0.65rem", color: "#999" }}>Leads</div>
                                    </div>
                                </div>
                            ))}
                            <button className="btn btn-sm w-100 mt-2" style={{
                                fontSize: "0.78rem",
                                border: "1px solid #ddd",
                                color: "#555",
                                fontWeight: 500,
                            }}>
                                Ver todas las campañas →
                            </button>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-lg-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                Alertas <Icono.AlertTriangle color="#aaa" width={14} height={14} />
                            </h6>
                            {alertas.length === 0 ? (
                                <p className="text-muted" style={{ fontSize: "0.82rem" }}>Sin alertas activas.</p>
                            ) : alertas.slice(0, 3).map((a, i) => (
                                <div key={i} className="d-flex gap-3 mb-3">
                                    <IconBubble color={a.color} size={32}>{a.icono}</IconBubble>
                                    <div>
                                        <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#222" }}>{a.titulo}</div>
                                        <div style={{ fontSize: "0.75rem", color: "#666", lineHeight: 1.5 }}>{a.detalle}</div>
                                    </div>
                                </div>
                            ))}
                            <button className="btn btn-sm w-100 mt-2" style={{
                                fontSize: "0.78rem",
                                border: "1px solid #ddd",
                                color: "#555",
                                fontWeight: 500,
                            }}>
                                Ver todas las alertas →
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
